import Flag from "react-world-flags";

import {
  ISO3_TO_ISO2_MAP,
  PAYOUT_WIZARD_COUNTRIES,
} from "@/modules/dashboard/dispersions/constants/dispersions.constants";
import {
  type ChoiceCardOption,
  ColocaChoiceCardField,
} from "@/shared/components/forms/ColocaChoiceCardField";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import type { CorridorOption } from "../../hooks/useBatchSetupOptions";

export interface CorridorChoiceFieldProps {
  options: CorridorOption[];
  disabled?: boolean;
}

/** One card per payout corridor (country × rail); a corridor without a route or with its rail off is disabled. */
export function CorridorChoiceField({ options, disabled }: CorridorChoiceFieldProps) {
  const { t } = useTranslations("payoutBatches");
  const countryLabel = useCountryLabel();

  const items: ChoiceCardOption[] = options.map((option) => {
    const fallback =
      PAYOUT_WIZARD_COUNTRIES.find((country) => country.code === option.country)?.name ?? option.country;
    return {
      value: option.value,
      label: (
        <span className="flex items-center gap-2">
          <Flag
            code={ISO3_TO_ISO2_MAP[option.country] ?? option.country}
            className="h-4 w-6 shrink-0 rounded object-cover"
            aria-hidden="true"
          />
          <span>
            {countryLabel(option.country, fallback)} · {formatRail(option.rail)}
          </span>
        </span>
      ),
      description: option.available
        ? t("setup_corridor_delivers", { currency: option.currency })
        : t("setup_corridor_unavailable"),
      disabled: !option.available,
    };
  });

  return (
    <ColocaChoiceCardField
      name="corridor"
      label={t("setup_corridor_label")}
      description={t("setup_corridor_description")}
      items={items}
      columns={2}
      disabled={disabled}
    />
  );
}
